import React, { useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Card,
  CardMedia,
  CardContent,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ImageUpload from "./ImageUpload";
import BatchImageUploadOCR from "./BatchImageUploadOCR";
import { getListings, getListingImages } from "./services/listingApi";

// Inline implementation of BatchImageUploadByPO so we don't need a separate file
const BatchImageUploadByPO = () => {
  const [poId, setPoId] = useState("");
  const [mode, setMode] = useState("front_only"); // "front_only" | "front_back"
  const [startIndex, setStartIndex] = useState(0);
  const [fromId, setFromId] = useState(""); // optional ID range
  const [toId, setToId] = useState(""); // optional ID range
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Preview images state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);

    if (!poId) {
      setError("PO ID is required.");
      return;
    }
    if (!files.length) {
      setError("Please select one or more images.");
      return;
    }

    // Basic client-side sanity check for range
    if (fromId && toId) {
      const startNum = Number(fromId);
      const endNum = Number(toId);
      if (!Number.isNaN(startNum) && !Number.isNaN(endNum) && startNum > endNum) {
        setError("From ID must be less than or equal to To ID.");
        return;
      }
    }

    const formData = new FormData();
    formData.append("po_id", poId);
    formData.append("mode", mode);
    formData.append("start_index", String(startIndex || 0));

    // send optional ID range through to backend
    if (fromId) formData.append("from_id", String(fromId));
    if (toId) formData.append("to_id", String(toId));

    files.forEach((file) => {
      formData.append("images[]", file);
    });

    setLoading(true);
    try {
      const res = await fetch("/api/upload_listing_images_batch_by_po", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Upload failed with status ${res.status}`);
      }

      const data = await res.json();
      setResult(data);
    } catch (err) {
      console.error("Batch PO upload failed:", err);
      setError(err.message || "Batch PO upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  const handlePreviewImages = async () => {
    setError("");
    if (!poId) {
      setError("PO ID is required to preview images.");
      return;
    }

    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewItems([]);

    try {
      const allListings = await getListings();
      const listingsArr = Array.isArray(allListings) ? allListings : allListings?.data || [];

      const filtered = listingsArr.filter((row) => {
        const poValue = row.purchase_order_id ?? row.po_id;
        if (!poValue) return false;
        if (String(poValue) !== String(poId)) return false;

        const listingId = row.id ?? row.listing_id;
        if (fromId) {
          const fromNum = Number(fromId);
          if (!Number.isNaN(fromNum) && Number(listingId) < fromNum) return false;
        }
        if (toId) {
          const toNum = Number(toId);
          if (!Number.isNaN(toNum) && Number(listingId) > toNum) return false;
        }
        return true;
      });

      const items = [];
      for (const row of filtered) {
        const listingId = row.id ?? row.listing_id;
        let imageUrl = "";

        if (listingId != null) {
          try {
            const imgData = await getListingImages(listingId);
            const urls = Array.isArray(imgData)
              ? imgData
              : imgData?.image_urls || imgData?.images || [];
            if (Array.isArray(urls) && urls.length > 0) {
              imageUrl = urls[0];
            }
          } catch (e) {
            console.error("Failed to load images for listing", listingId, e);
          }
        }

        items.push({
          id: listingId,
          name:
            row.card_name ||
            row.listing_title ||
            row.title ||
            "Unnamed card",
          imageUrl,
        });
      }

      setPreviewItems(items);
      if (!items.length) {
        setError("No listings found for this PO / range.");
      }
    } catch (e) {
      console.error("Preview images failed:", e);
      setError(e.message || "Failed to load preview images.");
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Typography variant="h6">Batch Upload by PO</Typography>

      <TextField
        label="PO ID"
        value={poId}
        onChange={(e) => setPoId(e.target.value)}
        size="small"
        required
      />

      <FormControl size="small">
        <InputLabel id="mode-label">Mode</InputLabel>
        <Select
          labelId="mode-label"
          label="Mode"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <MenuItem value="front_only">
            Front only (1 photo per listing)
          </MenuItem>
          <MenuItem value="front_back">
            Front + Back (2 photos per listing)
          </MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Start index (within PO)"
        type="number"
        size="small"
        value={startIndex}
        onChange={(e) => setStartIndex(Number(e.target.value) || 0)}
        helperText="0-based index of first listing within this PO for this batch"
      />

      {/* optional ID range within the PO */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <TextField
          label="From ID (optional)"
          type="number"
          size="small"
          value={fromId}
          onChange={(e) => setFromId(e.target.value)}
        />
        <TextField
          label="To ID (optional)"
          type="number"
          size="small"
          value={toId}
          onChange={(e) => setToId(e.target.value)}
        />
      </Box>

      {/* NEW: Preview Images button */}
      <Button
        variant="outlined"
        onClick={handlePreviewImages}
        disabled={!poId || previewLoading}
      >
        Preview Images
      </Button>

      <Button variant="outlined" component="label">
        Select Images
        <input
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleFileChange}
        />
      </Button>
      <Typography variant="body2">
        {files.length ? `${files.length} file(s) selected` : "No files selected"}
      </Typography>

      {loading && <LinearProgress />}

      {error && <Alert severity="error">{error}</Alert>}

      <Button
        type="submit"
        variant="contained"
        disabled={loading || !poId || !files.length}
      >
        Upload
      </Button>

      {result && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1">Result</Typography>
          <Typography variant="body2">
            Listings advanced in this batch: {result.listings_advanced ?? 0}
          </Typography>
          {Array.isArray(result.items) && (
            <Typography variant="body2">
              Linked:{" "}
              {result.items.filter((i) => i.status === "linked").length} |
              Unmatched extra:{" "}
              {result.items.filter(
                (i) => i.status === "unmatched_extra"
              ).length}
            </Typography>
          )}
        </Box>
      )}

      {/* Preview dialog: mobile-friendly, shows ID, card name, and image */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Preview Images</DialogTitle>
        <DialogContent dividers>
          {previewLoading && <LinearProgress sx={{ mb: 2 }} />}

          {!previewLoading && (!previewItems || previewItems.length === 0) && (
            <Typography variant="body2">
              No listings with images found for this PO.
            </Typography>
          )}

          {previewItems && previewItems.length > 0 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {previewItems.map((item) => (
                <Grid item xs={6} sm={4} md={3} key={item.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {item.imageUrl ? (
                      <CardMedia
                        component="img"
                        image={item.imageUrl}
                        alt={item.name}
                        sx={{ objectFit: "contain", maxHeight: 160 }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 160,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="caption">No image</Typography>
                      </Box>
                    )}
                    <CardContent sx={{ p: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        ID: {item.id}
                      </Typography>
                      <Typography
                        variant="body2"
                        noWrap
                        title={item.name}
                      >
                        {item.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Inline implementation of BatchImageUploadByConsignment (mirrors PO flow)
const BatchImageUploadByConsignment = () => {
  const [sessionId, setSessionId] = useState("");
  const [mode, setMode] = useState("front_only"); // "front_only" | "front_back"
  const [startIndex, setStartIndex] = useState(0);
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Preview images state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);

    if (!sessionId) {
      setError("Consignment Session ID is required.");
      return;
    }
    if (!files.length) {
      setError("Please select one or more images.");
      return;
    }

    // Basic client-side sanity check for range
    if (fromId && toId) {
      const startNum = Number(fromId);
      const endNum = Number(toId);
      if (!Number.isNaN(startNum) && !Number.isNaN(endNum) && startNum > endNum) {
        setError("From ID must be less than or equal to To ID.");
        return;
      }
    }

    const formData = new FormData();
    formData.append("consignment_session_id", sessionId);
    formData.append("mode", mode);
    formData.append("start_index", String(startIndex || 0));
    if (fromId) formData.append("from_id", String(fromId));
    if (toId) formData.append("to_id", String(toId));

    files.forEach((file) => {
      formData.append("images[]", file);
    });

    setLoading(true);
    try {
      const res = await fetch("/api/upload_listing_images_batch_by_consignment", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Upload failed with status ${res.status}`);
      }

      const data = await res.json();
      setResult(data);
    } catch (err) {
      console.error("Batch consignment upload failed:", err);
      setError(err.message || "Batch consignment upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  const handlePreviewImages = async () => {
    setError("");
    if (!sessionId) {
      setError("Consignment Session ID is required to preview images.");
      return;
    }

    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewItems([]);

    try {
      const allListings = await getListings();
      const listingsArr = Array.isArray(allListings) ? allListings : allListings?.data || [];

      const filtered = listingsArr.filter((row) => {
        const csValue = row.consignment_session_id;
        if (!csValue) return false;
        if (String(csValue) !== String(sessionId)) return false;

        const listingId = row.id ?? row.listing_id;
        if (fromId) {
          const fromNum = Number(fromId);
          if (!Number.isNaN(fromNum) && Number(listingId) < fromNum) return false;
        }
        if (toId) {
          const toNum = Number(toId);
          if (!Number.isNaN(toNum) && Number(listingId) > toNum) return false;
        }
        return true;
      });

      const items = [];
      for (const row of filtered) {
        const listingId = row.id ?? row.listing_id;
        let imageUrl = "";

        if (listingId != null) {
          try {
            const imgData = await getListingImages(listingId);
            const urls = Array.isArray(imgData)
              ? imgData
              : imgData?.image_urls || imgData?.images || [];
            if (Array.isArray(urls) && urls.length > 0) {
              imageUrl = urls[0];
            }
          } catch (e) {
            console.error("Failed to load images for listing", listingId, e);
          }
        }

        items.push({
          id: listingId,
          name: row.card_name || row.listing_title || row.title || "Unnamed card",
          imageUrl,
        });
      }

      setPreviewItems(items);
      if (!items.length) {
        setError("No listings found for this Consignment Session / range.");
      }
    } catch (e) {
      console.error("Preview images failed:", e);
      setError(e.message || "Failed to load preview images.");
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Typography variant="h6">Batch Upload by Consignment</Typography>

      <TextField
        label="Consignment Session ID"
        value={sessionId}
        onChange={(e) => setSessionId(e.target.value)}
        size="small"
        required
      />

      <FormControl size="small">
        <InputLabel id="mode-label-consignment">Mode</InputLabel>
        <Select
          labelId="mode-label-consignment"
          label="Mode"
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <MenuItem value="front_only">
            Front only (1 photo per listing)
          </MenuItem>
          <MenuItem value="front_back">
            Front + Back (2 photos per listing)
          </MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Start index (within Session)"
        type="number"
        size="small"
        value={startIndex}
        onChange={(e) => setStartIndex(Number(e.target.value) || 0)}
        helperText="0-based index of first listing within this session for this batch"
      />

      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <TextField
          label="From ID (optional)"
          type="number"
          size="small"
          value={fromId}
          onChange={(e) => setFromId(e.target.value)}
        />
        <TextField
          label="To ID (optional)"
          type="number"
          size="small"
          value={toId}
          onChange={(e) => setToId(e.target.value)}
        />
      </Box>

      <Button
        variant="outlined"
        onClick={handlePreviewImages}
        disabled={!sessionId || previewLoading}
      >
        Preview Images
      </Button>

      <Button variant="contained" component="label">
        Select Images
        <input
          type="file"
          hidden
          multiple
          accept="image/*"
          onChange={handleFileChange}
        />
      </Button>

      {files.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {files.length} file(s) selected
        </Typography>
      )}

      <Button
        type="submit"
        variant="contained"
        disabled={loading || !files.length || !sessionId}
      >
        Upload
      </Button>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {result && (
        <Alert severity="success">
          Uploaded: {result?.uploaded_count ?? 0} images. Linked: {result?.linked_count ?? 0}.
        </Alert>
      )}

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview Images (Consignment Session)</DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <Box sx={{ py: 2 }}><LinearProgress /></Box>
          ) : (
            <Grid container spacing={2}>
              {previewItems.map((item) => (
                <Grid item xs={6} sm={4} md={3} key={item.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {item.imageUrl ? (
                      <CardMedia
                        component="img"
                        image={item.imageUrl}
                        alt={item.name}
                        sx={{ objectFit: "contain", maxHeight: 160 }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 160,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="caption">No image</Typography>
                      </Box>
                    )}
                    <CardContent sx={{ p: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        ID: {item.id}
                      </Typography>
                      <Typography variant="body2" noWrap title={item.name}>
                        {item.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};


const ListingImagesPanel = () => {
  const [tabIndex, setTabIndex] = useState(0);

  const handleChange = (e, newValue) => {
    setTabIndex(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs value={tabIndex} onChange={handleChange} sx={{ mb: 2 }}>
        <Tab label="Single Upload" />
        <Tab label="Batch Upload by PO" />
        <Tab label="Batch Upload by Consignment" />
        <Tab label="Batch Label OCR" />
      </Tabs>

      {tabIndex === 0 && <ImageUpload />}
      {tabIndex === 1 && <BatchImageUploadByPO />}
      {tabIndex === 2 && <BatchImageUploadByConsignment />}
      {tabIndex === 3 && <BatchImageUploadOCR />}
    </Box>
  );
};

export default ListingImagesPanel;
